import * as Yup from "yup";
import {
  type Householder,
  type HouseholderEntryPage,
  type HouseholderQuote,
  HouseholderType,
} from "@/api_client";
import { getErrorMessage } from "@/utils/errorMessage";
import { AgeValidator } from "@/validators/shared/ageValidators";
import { validSSN } from "@/validators/shared/ssnValidator";

const HouseholdEntryValidationSchema = (
  content?: HouseholderEntryPage | null,
): Yup.ObjectSchema<
  Omit<
    Householder,
    "streetAddress" | "postalCode" | "city" | "householderInsuredWithIf"
  >
> =>
  Yup.object({
    householderType: Yup.mixed<HouseholderType>().required(
      getErrorMessage(
        content?.householderTypeQuestion?.validationMessages?.value,
        "required",
      ),
    ),
    livingSpace: Yup.number()
      .typeError(
        getErrorMessage(
          content?.livingSpaceQuestion?.validationMessages?.value,
          "required",
        ),
      )
      .required(
        getErrorMessage(
          content?.livingSpaceQuestion?.validationMessages?.value,
          "required",
        ),
      )
      .min(
        4,
        getErrorMessage(
          content?.livingSpaceQuestion?.validationMessages?.value,
          "minimum",
        ),
      )
      .max(
        400,
        getErrorMessage(
          content?.livingSpaceQuestion?.validationMessages?.value,
          "maximum",
        ),
      )
      .test(
        "wrong-format",
        getErrorMessage(
          content?.livingSpaceQuestion?.validationMessages?.value,
          "wrong-format",
        ),
        (val) => {
          const matched = /^\d+$/.exec(val.toString());
          return matched !== undefined && matched !== null;
        },
      ),
    isHousingCompany: Yup.boolean().optional(),
  });

export const HouseholderEntryValidationSchema = (
  content?: HouseholderEntryPage | null,
): Yup.ObjectSchema<
  Pick<
    HouseholderQuote,
    "productType" | "householder" | "policyHolderSsn" | "isHousingCompany"
  >
> =>
  Yup.object({
    productType: Yup.string().oneOf(["householder"]).required(),
    householder: HouseholdEntryValidationSchema(content).required(),
    policyHolderSsn: Yup.string()
      .required(
        getErrorMessage(
          content?.entryPageCommon?.ssnQuestion?.validationMessages?.value,
          "required",
        ),
      )
      .test(
        "valid-ssn",
        getErrorMessage(
          content?.entryPageCommon?.ssnQuestion?.validationMessages?.value,
          "valid-ssn",
        ),
        validSSN,
      )
      .test(
        "is-18+",
        getErrorMessage(
          content?.entryPageCommon?.ssnQuestion?.validationMessages?.value,
          "is-18+",
        ),
        (ssn: string) => {
          const ageValidator = new AgeValidator(ssn);
          return ageValidator.isAtLeast18;
        },
      ),
    isHousingCompany: Yup.boolean().when("householder.householderType", {
      is: HouseholderType.PAIR_HOUSE,
      then: (schema) =>
        schema
          .required(
            getErrorMessage(
              content?.isHousingCompanyQuestion?.validationMessages?.value,
              "required",
            ),
          )
          .oneOf(
            [true],
            getErrorMessage(
              content?.isHousingCompanyQuestion?.validationMessages?.value,
              "not-housing-company",
            ),
          ),
      otherwise: (schema) => schema.optional(),
    }),
  });
